import { useCallback, useState } from 'react';

import { useImageConverter } from '../../../services/hooks';
import type { FileInputProps } from '../FileInput.intefaces';

type UseFileInputUploadOptions = Pick<
  FileInputProps,
  'onChange' | 'cache' | 'validators'
> &
  Required<
    Pick<
      FileInputProps,
      'loading' | 'onBlur' | 'onCacheSuccess' | 'onCacheError'
    >
  > & {
    onDocumentChange: (blob: Blob) => void;
  };

export const useFileInputUpload = ({
  onChange,
  onBlur,
  loading,
  cache,
  onCacheSuccess,
  onCacheError,
  validators,
  onDocumentChange,
}: UseFileInputUploadOptions) => {
  const imageConverter = useImageConverter();
  const [isFileLoading, setIsFileLoading] = useState(loading);
  const [validationErrorMessage, setValidationErrorMessage] = useState('');
  const [invalidFileName, setInvalidFileName] = useState('');

  const isHEICFile = (file: File) => {
    const fileName = file.name.toLowerCase();

    const isHEICByExtension =
      fileName.endsWith('.heic') || fileName.endsWith('.heif');

    const isHEICByType = [
      'image/heic',
      'image/heif',
      'image/heif-sequence',
      'image/heic-sequence',
    ].includes(file.type);

    const isHEICOnWindows =
      (file.type === '' || file.type === 'application/octet-stream') &&
      isHEICByExtension;

    return isHEICByExtension || isHEICByType || isHEICOnWindows;
  };

  const isConvertableImageFile = (file: File) => {
    const fileName = file.name.toLowerCase();

    const isConvertableByExtension =
      fileName.endsWith('.dng') || fileName.endsWith('.tiff');

    const isConvertableByType = [
      'image/tiff',
      'image/dng',
      'image/x-adobe-dng',
    ].includes(file.type);

    return isConvertableByType || isConvertableByExtension;
  };

  const clearValidationState = useCallback(() => {
    setValidationErrorMessage('');
    setInvalidFileName('');
  }, []);

  const validateUploadedFile = useCallback(
    async (uploadedFile: File) => {
      if (!validators) {
        return false;
      }

      try {
        const validationResult = await validators({ source: uploadedFile });

        if (validationResult) {
          setValidationErrorMessage(validationResult);
          setInvalidFileName(uploadedFile.name);
          return true;
        }

        clearValidationState();
        return false;
      } catch (validationError) {
        setValidationErrorMessage('Ошибка при проверке файла');
        setInvalidFileName(uploadedFile.name);
        return true;
      }
    },
    [validators, clearValidationState],
  );

  const handleCachedUpload = useCallback(
    async (uploadedFile: File, isWrongFormat: boolean) => {
      if (!cache) {
        return;
      }

      if (isWrongFormat) {
        const [fileCacheResponse, blob] = await Promise.all([
          cache(uploadedFile).catch(() => null),

          imageConverter.convert(uploadedFile).catch(() => null),
        ]);

        const cacheId = fileCacheResponse?.file_id;
        const cacheExpiredAt = fileCacheResponse?.expired_at;

        const convertedFileName = uploadedFile.name.replace(
          /\.[^.]+$/,
          '.jpeg',
        );

        const convertedFile =
          blob &&
          new File([blob], convertedFileName, {
            type: blob.type,
          });

        onDocumentChange(convertedFile || new Blob());

        if (convertedFile && validators) {
          const validationResult = await validators({
            source: convertedFile,
          });

          if (validationResult) {
            setValidationErrorMessage(validationResult);
            setInvalidFileName(uploadedFile.name);
            return;
          }
        }

        onChange({
          value: cacheId
            ? JSON.stringify({
                type: 'file_cache',
                id: cacheId,
                expiredAt: cacheExpiredAt || '',
              })
            : convertedFile || uploadedFile,
          source: convertedFile || uploadedFile,
        });
      } else {
        try {
          const { file_id: cacheId, expired_at: cacheExpiredAt } =
            await cache(uploadedFile);

          onDocumentChange(uploadedFile);
          onChange({
            value: JSON.stringify({
              type: 'file_cache',
              id: cacheId,
              expiredAt: cacheExpiredAt,
            }),
            source: uploadedFile,
          });
          onCacheSuccess(cacheId.toString());
        } catch (e) {
          onCacheError();
        }
      }
    },
    [
      cache,
      onChange,
      imageConverter,
      onCacheError,
      onCacheSuccess,
      validators,
      onDocumentChange,
    ],
  );

  const handleNonCachedUpload = useCallback(
    async (uploadedFile: File, isWrongFormat: boolean) => {
      if (isWrongFormat) {
        const blob = await imageConverter.convert(uploadedFile);

        const convertedFileName = uploadedFile.name.replace(
          /\.[^.]+$/,
          '.jpeg',
        );

        const convertedFile = new File([blob], convertedFileName, {
          type: blob.type,
        });

        if (validators) {
          const validationResult = await validators({
            source: convertedFile,
          });

          if (validationResult) {
            setValidationErrorMessage(validationResult);
            setInvalidFileName(uploadedFile.name);
            return;
          }
        }

        onDocumentChange(blob);
        onChange({
          value: convertedFile,
          source: convertedFile,
        });
      } else {
        onDocumentChange(uploadedFile);
        onChange({
          value: uploadedFile,
          source: uploadedFile,
        });
      }
    },
    [onChange, imageConverter, validators, onDocumentChange],
  );

  /**
   * Функция onDrop обрабатывает перетаскивание файлов.
   * Если файлы перетащены, устанавливает флаг загрузки (isFileLoading) в true.
   * Проверяет формат загруженного файла на соответствие разрешённым типам.
   * Если использование кэша (cache) разрешено и формат файла неподдерживается,
   * конвертирует файл с помощью ImageConverter и кэширует его.
   * Устанавливает конвертированный или исходный файл как blobDocument
   * и вызывает onChange с новым значением.
   * Если формат файла поддерживается и кэш разрешён, кэширует файл и вызывает onChange.
   * Если использование кэша запрещено, конвертирует файл (если формат неподдерживается)
   * и вызывает onChange с новым значением.
   * Устанавливает флаг загрузки (isFileLoading) в false.
   * Если файлы не перетащены, вызывает onChange с undefined.
   * Снимает фокус с активного элемента и вызывает onBlur.
   *
   * @param files - Массив загруженных файлов.
   */
  const onDrop = useCallback(
    async (files: File[]) => {
      try {
        if (files && files.length) {
          setIsFileLoading(true);
          clearValidationState();

          const uploadedFile = files[0];
          const isWrongFormat =
            isHEICFile(uploadedFile) || isConvertableImageFile(uploadedFile);

          if (!isWrongFormat) {
            const validationFailed = await validateUploadedFile(uploadedFile);

            if (validationFailed) {
              setIsFileLoading(false);
              return;
            }
          }

          if (cache) {
            await handleCachedUpload(uploadedFile, isWrongFormat);
          } else {
            await handleNonCachedUpload(uploadedFile, isWrongFormat);
          }

          setIsFileLoading(false);
        } else {
          onChange(undefined);
        }

        (document?.activeElement as Element & { blur: () => void })?.blur();
        onBlur();
      } catch {
        const uploadedFile = files[0];

        setValidationErrorMessage('Ошибка при загрузке файла');
        setInvalidFileName(uploadedFile.name);
        setIsFileLoading(false);
      }
    },
    [
      cache,
      clearValidationState,
      handleCachedUpload,
      onChange,
      handleNonCachedUpload,
      onBlur,
      validateUploadedFile,
    ],
  );

  return { isFileLoading, validationErrorMessage, invalidFileName, onDrop };
};
